import React, { useEffect, useState } from "react";
import { api, formatApiError, formatRupiah } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import HistoryButton from "@/components/HistoryButton";

export default function Kasbon() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const [k, u] = await Promise.all([api.get("/kasbon"), api.get("/users").catch(() => ({ data: [] }))]);
    setItems(k.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/kasbon", { ...form, amount: Number(form.amount), user_id: form.user_id || user.id });
      toast.success("Kasbon diajukan");
      setForm({}); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const approve = async (id, status) => {
    try {
      await api.post(`/kasbon/${id}/approve`, { status, note: "" });
      toast.success(status === "approved" ? "Disetujui" : "Ditolak");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const canApprove = ["admin", "hr", "finance"].includes(user?.role);

  return (
    <div>
      <PageHeader
        title="Kasbon"
        subtitle="Pengajuan pinjaman karyawan dengan approval"
        testId="kasbon-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="kasbon-add-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold"><Plus size={16} className="mr-1" /> Ajukan Kasbon</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl">Ajukan Kasbon</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                {canApprove && (
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Karyawan</Label>
                    <Select value={form.user_id || ""} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="kasbon-field-user"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Tanggal</Label>
                  <Input required type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Jumlah (Rp)</Label>
                  <Input required type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="kasbon-field-amount" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-400">Alasan</Label>
                  <Input required value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="kasbon-field-reason" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                  <Button type="submit" data-testid="kasbon-submit-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">Ajukan</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <Section>
        {items.length === 0 ? <Empty /> : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tanggal</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Karyawan</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Jumlah</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Alasan</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Status</TableHead>
                  {canApprove && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((k) => (
                  <TableRow key={k.id} className="border-neutral-800" data-testid={`kasbon-row-${k.id}`}>
                    <TableCell className="font-mono text-xs">{k.date}</TableCell>
                    <TableCell className="font-mono">{users.find((u) => u.id === k.user_id)?.name || "-"}</TableCell>
                    <TableCell className="font-mono font-bold">{formatRupiah(k.amount)}</TableCell>
                    <TableCell className="font-mono text-xs">{k.reason}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-mono uppercase px-2 py-1 border ${
                        k.status === "approved" ? "border-[#16A34A] text-[#16A34A]" :
                        k.status === "rejected" ? "border-[#DC2626] text-[#DC2626]" :
                        "border-[#FFD700] text-[#FFD700]"
                      }`}>{k.status}</span>
                    </TableCell>
                    {canApprove && (
                      <TableCell className="text-right space-x-2">
                        <HistoryButton entityType="kasbon" entityId={k.id} />
                        {k.status === "pending" && (
                          <>
                            <button onClick={() => approve(k.id, "approved")} data-testid={`kasbon-approve-${k.id}`} className="text-[#16A34A] hover:text-white"><Check size={16} /></button>
                            <button onClick={() => approve(k.id, "rejected")} data-testid={`kasbon-reject-${k.id}`} className="text-[#DC2626] hover:text-white"><X size={16} /></button>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
